<template>
  <ContestEntryFormLayout
    :load-state="loadState"
    :error-message="errorMessage"
    :current-step="3"
    :title="t('contestEntryForm.step3.title')"
    :contest="microCmsContest"
  >
    <ContestEntryConfirmForm
      v-if="applicantForm && workForm"
      :applicant-form="applicantForm"
      :work-form="workForm"
      :category-label="categoryLabel"
      :extra-field-items="extraFieldItems"
      :submitting="submitting"
      :submit-error="submitError"
      @back="onClickBack"
      @submit="onClickSubmit"
    />
  </ContestEntryFormLayout>
</template>

<script lang="ts">
export default {
  metaInfo: {
    meta: [{ name: "robots", content: "none" }],
  },
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n-bridge";
import { NolaPfContestClient } from "@/lib/clients";
import { NolaPfMicroCmsContest } from "@/lib/models/nolaPfContest";
import { findContestDepartmentName, toContestDepartmentOptions } from "@/lib/utils/contestEntryDepartments";
import {
  clearContestEntryFormHistoryState,
  pushWithContestEntryForms,
  resolveApplicantFormHistoryState,
  resolveWorkFormHistoryState,
} from "@/lib/utils/contestEntryFormHistory";
import { buildCreateContestEntryInput, readContestEntryErrorMessage } from "@/lib/utils/contestEntryRequest";
import { toContestExtraAnswers, visibleExtraFieldItems } from "@/lib/utils/contestEntryExtraFields";
import { isContestEntryWorkFormComplete } from "@/lib/utils/contestEntryWorkForm";
import type { ContestEntryApplicantFormValue } from "@/components/organisms/event/contestEntryApplicantInfoFormEventHandlers";
import ContestEntryConfirmForm from "@/components/organisms/event/ContestEntryConfirmForm.vue";
import ContestEntryFormLayout from "@/components/templates/ContestEntryFormLayout.vue";
import type { ContestEntryWorkFormValue } from "@/components/organisms/event/contestEntryWorkFormTypes";

type LoadState = "loading" | "loaded" | "error";

const nolaPfContestClient = new NolaPfContestClient();

const instance = getCurrentInstance();
const { t } = useI18n();

const loadState = ref<LoadState>("loading");
const errorMessage = ref("");
const microCmsContest = ref<NolaPfMicroCmsContest | null>(null);
const applicantForm = ref<ContestEntryApplicantFormValue | null>(null);
const workForm = ref<ContestEntryWorkFormValue | null>(null);
const submitting = ref(false);
const submitError = ref("");

const contestId = computed(() => instance?.proxy.$route.params.id as string);

const extraFieldItems = computed(() => visibleExtraFieldItems(microCmsContest.value?.extraFieldItems));

const categoryLabel = computed(() =>
  findContestDepartmentName(microCmsContest.value?.departments, workForm.value?.categoryId ?? "")
);

const step2Location = computed(() => ({
  name: "eventContestEntryFormStep2",
  params: { id: contestId.value },
}));

async function loadPageData() {
  const id = contestId.value;
  if (!id) {
    errorMessage.value = t("contestEntryForm.step3.loadError");
    loadState.value = "error";
    return;
  }

  const applicant = resolveApplicantFormHistoryState(id);
  const work = resolveWorkFormHistoryState(id);
  if (!applicant || !work || !isContestEntryWorkFormComplete(work, { categoryRequired: false })) {
    errorMessage.value = t("contestEntryForm.step3.loadError");
    loadState.value = "error";
    return;
  }

  applicantForm.value = applicant;
  workForm.value = work;
  loadState.value = "loading";

  try {
    const microCmsContestResult = await nolaPfContestClient.fetchMicroCmsContest(id);
    microCmsContest.value = microCmsContestResult;
    const categoryRequired = toContestDepartmentOptions(microCmsContestResult.departments).length > 0;
    if (!isContestEntryWorkFormComplete(work, { categoryRequired })) {
      errorMessage.value = t("contestEntryForm.step3.loadError");
      loadState.value = "error";
      return;
    }

    loadState.value = "loaded";
  } catch (error) {
    console.error("Failed to load contest entry form step3:", error);
    errorMessage.value = t("contestEntryForm.step3.contestLoadError");
    loadState.value = "error";
  }
}

async function onClickBack() {
  const id = contestId.value;
  const applicant = applicantForm.value;
  const work = workForm.value;
  if (!id || !applicant || !work) {
    await instance!.proxy.$router.push(step2Location.value);
    return;
  }

  await pushWithContestEntryForms((location) => instance!.proxy.$router.push(location), step2Location.value, id, {
    applicantForm: applicant,
    workForm: work,
  });
}

async function onClickSubmit() {
  if (submitting.value) return;

  const applicant = applicantForm.value;
  const work = workForm.value;
  const platformContestId = microCmsContest.value?.contestId;
  if (!applicant || !work?.novelId || !platformContestId) {
    submitError.value = t("contestEntryForm.step3.submitError");
    return;
  }

  submitting.value = true;
  submitError.value = "";

  try {
    await nolaPfContestClient.createEntry(
      buildCreateContestEntryInput({
        contestId: platformContestId,
        novelId: work.novelId,
        categoryId: work.categoryId,
        workTitle: work.workTitle,
        summary: work.synopsis,
        applicant,
        extraAnswers: toContestExtraAnswers(microCmsContest.value?.extraFieldItems, work.extraAnswers),
      })
    );
    clearContestEntryFormHistoryState();
    await instance!.proxy.$router.push({
      name: "eventContestEntryFormCompleted",
      params: { id: contestId.value },
    });
  } catch (error) {
    console.error("Failed to submit contest entry:", error);
    submitError.value = readContestEntryErrorMessage(error) ?? t("contestEntryForm.step3.submitError");
  } finally {
    submitting.value = false;
  }
}

onMounted(() => {
  loadPageData();
});
</script>

